/**
 * Володіння локальними медіа.
 *
 * Єдиний власник потоків: він їх створює, він і зупиняє. На мобільному клієнті
 * розмите володіння призводило до того, що один і той самий потік звільнявся
 * до чотирьох разів за одне завершення дзвінка — тут цього шляху немає за
 * побудовою. Решта коду посилання лише читає й обнуляє.
 */

export type MediaFailureKind =
  /** Користувач відмовив у доступі. */
  | 'denied'
  /** Пристрою немає, він зайнятий або не задовольняє обмеження. */
  | 'unavailable';

export class MediaFailure extends Error {
  constructor(
    readonly kind: MediaFailureKind,
    message: string,
  ) {
    super(message);
    this.name = 'MediaFailure';
  }
}

/**
 * Класифікує помилку `getUserMedia`.
 *
 * Відмова в дозволі й відсутній пристрій ведуть до РІЗНИХ повідомлень: у
 * першому випадку користувачу треба змінити дозвіл, у другому — перевірити
 * камеру. Мобільний клієнт зводить усе в «немає дозволів», і це збиває з пантелику.
 */
export function classifyMediaError(error: unknown): MediaFailure {
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new MediaFailure('denied', 'доступ до камери й мікрофона відхилено');
    case 'NotFoundError':
    case 'NotReadableError':
    case 'OverconstrainedError':
    case 'AbortError':
      return new MediaFailure('unavailable', 'камера або мікрофон недоступні');
    default:
      return new MediaFailure('unavailable', 'не вдалося отримати медіа');
  }
}

export interface MediaGateway {
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  getDisplayMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
}

const browserGateway: MediaGateway = {
  getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
  getDisplayMedia: (constraints) => navigator.mediaDevices.getDisplayMedia(constraints),
};

const DEFAULT_CONSTRAINTS: MediaStreamConstraints = {
  audio: true,
  video: { facingMode: 'user' },
};

export interface MediaSnapshot {
  micEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
}

export class MediaController {
  private local: MediaStream | null = null;
  private screen: MediaStream | null = null;
  /**
   * Цільовий стан тримаємо самі, а не читаємо з `track.enabled`.
   * У Safari `enabled = true` не завжди повертає джерело до життя, тож трек —
   * ненадійне джерело істини про намір користувача.
   */
  private micOn = true;
  private videoOn = true;

  constructor(
    private readonly gateway: MediaGateway = browserGateway,
    private readonly onScreenShareEnded: () => void = () => undefined,
  ) {}

  get stream(): MediaStream | null {
    return this.local;
  }

  get snapshot(): MediaSnapshot {
    return {
      micEnabled: this.micOn,
      videoEnabled: this.videoOn,
      screenSharing: this.screen !== null,
    };
  }

  /**
   * Запитує камеру й мікрофон. Викликається ЛИШЕ з дії користувача:
   * у Safari `getUserMedia` поза жестом відхиляється.
   */
  async acquire(): Promise<MediaStream> {
    if (this.local) return this.local;
    try {
      const stream = await this.gateway.getUserMedia(DEFAULT_CONSTRAINTS);
      this.local = stream;
      // Новий дзвінок завжди стартує з увімкненими мікрофоном і камерою:
      // стан попереднього дзвінка свідомо не відновлюється.
      this.micOn = true;
      this.videoOn = true;
      this.applyEnabledState();
      return stream;
    } catch (error) {
      throw classifyMediaError(error);
    }
  }

  setMicEnabled(enabled: boolean): void {
    this.micOn = enabled;
    this.applyEnabledState();
  }

  setVideoEnabled(enabled: boolean): void {
    this.videoOn = enabled;
    this.applyEnabledState();
  }

  private applyEnabledState(): void {
    const stream = this.local;
    if (!stream) return;
    for (const track of stream.getTracks()) {
      track.enabled = track.kind === 'audio' ? this.micOn : this.videoOn;
    }
  }

  /**
   * Вмикає демонстрацію екрана. Повертає трек для `replaceTrack()` —
   * зʼєднання не перестворюється.
   */
  async startScreenShare(): Promise<MediaStreamTrack | null> {
    if (this.screen) return this.screen.getVideoTracks()[0] ?? null;
    let stream: MediaStream;
    try {
      stream = await this.gateway.getDisplayMedia({ video: true });
    } catch (error) {
      // Скасування вибору джерела — не помилка: користувач передумав.
      if (error instanceof Error && error.name === 'NotAllowedError') return null;
      throw classifyMediaError(error);
    }

    this.screen = stream;
    const track = stream.getVideoTracks()[0] ?? null;
    if (track) {
      // Зупинення системною кнопкою браузера має повертати камеру так само,
      // як і кнопка в застосунку.
      track.addEventListener('ended', () => {
        this.onScreenShareEnded();
      });
    }
    return track;
  }

  /** Зупиняє демонстрацію і повертає трек камери для зворотної заміни. */
  stopScreenShare(): MediaStreamTrack | null {
    const screen = this.screen;
    this.screen = null;
    if (screen) stopTracks(screen);
    return this.local?.getVideoTracks()[0] ?? null;
  }

  /**
   * Зупиняє всі локальні треки рівно один раз.
   *
   * Кожен трек зупиняється в ізоляції: збій на одному не має лишити решту
   * працювати, бо саме незупинений трек тримає індикатор камери увімкненим.
   */
  release(): void {
    const local = this.local;
    const screen = this.screen;
    this.local = null;
    this.screen = null;
    if (local) stopTracks(local);
    if (screen) stopTracks(screen);
  }
}

function stopTracks(stream: MediaStream): void {
  let tracks: MediaStreamTrack[];
  try {
    tracks = stream.getTracks();
  } catch {
    return;
  }
  for (const track of tracks) {
    try {
      track.stop();
    } catch {
      // Один збійний трек не має лишити решту камери увімкненою.
    }
  }
}
